import { useCallback, useState } from "react"
import { formatWithMask, type Mask } from "react-native-mask-input"

/**
 * useInputField data can be used to manage the state of an input field.
 * usage:
 * ```tex
 * const fieldData = useInputFieldData({ initialValue: "initial value" })
 * ...
 *    <BFTextInput className={INPUT_STYLES} {...fieldData} />
 * ```
 * @param initialValue the initial value of the input field
 * @param mask the mask to apply to the input field
 * @param validator a function that validates the input field
 * @param onValueChange a function that is called when the value of the input field changes
 */
export const useInputFieldData = ({
  initialValue,
  mask,
  validator,
  onValueChange,
}: {
  initialValue?: string
  mask?: Mask
  validator?: (value: string) => string | undefined
  onValueChange?: (value: string, error?: string) => void
}) => {
  const [value, setValue] = useState(
    mask
      ? formatWithMask({ text: initialValue || "", mask }).masked
      : initialValue || ""
  )
  const [unmaskedValue, setUnmaskedValue] = useState(initialValue || "")
  const [error, setError] = useState<string | undefined | null>(undefined)

  const onChangeText = useCallback(
    (text: string) => {
      setError(undefined)
      let textValue = text
      let newUnmasked = text
      if (mask) {
        const result = formatWithMask({
          text,
          mask,
        })
        textValue = result.masked
        newUnmasked = result.unmasked
        setUnmaskedValue(newUnmasked)
      } else {
        setUnmaskedValue(text)
      }
      setValue(textValue)

      const validationError = validator
        ? validator(newUnmasked)
        : undefined
      onValueChange?.(newUnmasked, validationError)
    },
    [setValue, mask, setUnmaskedValue, validator, onValueChange]
  )

  const onBlur = useCallback(() => {
    const error = validator ? validator(unmaskedValue) : undefined
    if (validator) {
      setError(error)
    }
    onValueChange?.(unmaskedValue, error)
  }, [validator, unmaskedValue, onValueChange])

  return {
    value: unmaskedValue,
    error,
    props: { onBlur, value, onChangeText },
  }
}
